import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react'
import { useSoundEffect } from 'src/hooks/useSoundEffect'
import { formatNumberWithSuffix } from 'src/utils/formatNumberWithSuffix'
import AngleLeftIcon from 'src/app/assets/icons/ico-angle-left.svg'
import AngleRightIcon from 'src/app/assets/icons/ico-angle-right.svg'
import { useClientSideTranslation } from 'src/services/i18n'
import { useGetCurrentLanguage } from 'src/store/game'
import { useDeviceInfo } from 'src/hooks/useDeviceInfo'
import {
  generateSmartChips,
  generateChipsFromMultipliers,
  hasCustomChipMultipliers,
  prependMinBetChip
} from 'src/utils/generateSmartChips'

interface BetChip {
  label: string
  value: number
  type: 'set' | 'increment'
}

type Props = Readonly<{
  currentValue: string
  onChange: (value: string) => void
  onValidatedChange: (value: string) => void
  minAmount: number
  maxAmount: number
  maxBalance: number
  chipMultipliers: number[]
  disabled?: boolean
}>

export function BetInput({
  chipMultipliers,
  currentValue,
  onChange,
  onValidatedChange,
  minAmount,
  maxAmount,
  maxBalance,
  disabled = false
}: Props) {
  const { isMobile } = useDeviceInfo()
  const { playEffect } = useSoundEffect()
  const [totalBet, setTotalBet] = useState(parseFloat(currentValue))
  const [lastClickedChip, setLastClickedChip] = useState<number | null>(null)
  const isChipClick = useRef(false)
  const currentLanguage = useGetCurrentLanguage()
  const { t } = useClientSideTranslation(currentLanguage)
  const [isSwiping, setIsSwiping] = useState(false)

  // High-watermark of the player's balance: grows when balance increases
  // (refresh-balance from the operator iframe, deposits, big wins), but
  // never shrinks so a single losing round doesn't strip larger chips
  // off the strip mid-session.
  const initialBalanceRef = useRef<number | null>(null)
  if (initialBalanceRef.current === null || maxBalance > initialBalanceRef.current) {
    initialBalanceRef.current = maxBalance
  }

  // Touch handling state
  const touchStartX = useRef<number | null>(null)
  const touchEndX = useRef<number | null>(null)
  const hasMoved = useRef(false)
  const MIN_SWIPE_DISTANCE = 50

  const VISIBLE_BUTTONS = isMobile ? 3 : 5

  // Generate chip values - use custom multipliers if set, otherwise smart generation
  const rawAmounts = useMemo(() => {
    const chips = hasCustomChipMultipliers(chipMultipliers)
      ? // Operator has custom multipliers - use them (existing behavior)
        generateChipsFromMultipliers(chipMultipliers, minAmount)
      : // Generate smart chips based on initial balance snapshot
        generateSmartChips({
          minBet: minAmount,
          maxBet: maxAmount,
          balance: initialBalanceRef.current ?? maxBalance
        })

    // When the min bet sits below the smallest clean chip, offer the exact
    // min as an extra leading chip so players can still place the minimum
    // (QA-60081).
    return prependMinBetChip(chips, minAmount)
  }, [chipMultipliers, minAmount, maxAmount, maxBalance])

  const betChips: BetChip[] = useMemo(() => {
    const base = rawAmounts
      .map((amount) => ({
        label: formatNumberWithSuffix(amount, t),
        value: amount,
        type: 'increment' as const
      }))
      .filter((chip) => chip.value > 0)

    return [
      ...base,
      { label: t('Max'), value: Math.min(maxAmount, maxBalance), type: 'set' as const }
    ]
  }, [maxAmount, maxBalance, rawAmounts, t])

  const getInitialIndex = useCallback(() => {
    const averageLikelyBet = Math.min(maxBalance * 0.1, maxAmount)
    let closestIndex = 0
    let smallestDiff = Infinity

    rawAmounts.forEach((value, index) => {
      const diff = Math.abs(value - averageLikelyBet)
      if (diff < smallestDiff) {
        smallestDiff = diff
        closestIndex = index
      }
    })

    const proposedIndex = closestIndex - Math.floor(VISIBLE_BUTTONS / 2)
    const maxStart = rawAmounts.length - VISIBLE_BUTTONS
    return Math.max(0, Math.min(proposedIndex, maxStart))
  }, [rawAmounts, maxBalance, maxAmount, VISIBLE_BUTTONS])

  const [startIndex, setStartIndex] = useState(getInitialIndex())

  useEffect(() => {
    const numValue = parseFloat(currentValue)
    if (!isNaN(numValue)) {
      setTotalBet(numValue)
      // Only clear last clicked chip when value changes from manual input, not from chip clicks
      if (!isChipClick.current) {
        setLastClickedChip(null)
      }
      isChipClick.current = false
    }
  }, [currentValue])

  useEffect(() => {
    setStartIndex(getInitialIndex())
  }, [getInitialIndex])

  const handleButtonClick = (amount: number, type: 'set' | 'increment') => {
    if (!hasMoved.current && !disabled) {
      playEffect('chip')
      isChipClick.current = true
      let newTotal: number
      if (type === 'set') {
        // Max/Min buttons always set directly
        newTotal = amount
        setLastClickedChip(null)
      } else if (lastClickedChip === amount) {
        // Same chip clicked again - increment
        newTotal = totalBet + amount
      } else {
        // Different chip clicked - set to that value
        newTotal = amount
        setLastClickedChip(amount)
      }
      setTotalBet(newTotal)
      onChange(newTotal.toString())
      onValidatedChange(newTotal.toString())
    }
  }

  const handleDouble = () => {
    if (!disabled) {
      playEffect('chip')
      const doubled = totalBet * 2
      const newTotal = Math.min(doubled, Math.min(maxAmount, maxBalance))
      setTotalBet(newTotal)
      onChange(newTotal.toString())
      onValidatedChange(newTotal.toString())
    }
  }

  const handleScroll = (direction: 'left' | 'right') => {
    if (!disabled) {
      playEffect('click')
      setStartIndex((prev) => {
        const maxStartIndex = betChips.length - VISIBLE_BUTTONS
        const newIndex =
          direction === 'left' ? Math.max(0, prev - 1) : Math.min(maxStartIndex, prev + 1)

        return newIndex
      })
    }
  }

  // Touch event handlers
  const handleTouchStart = (e: React.TouchEvent<HTMLDivElement>) => {
    const touch = e.touches[0]
    if (!touch) return

    touchStartX.current = touch.clientX
    touchEndX.current = null
    hasMoved.current = false
  }

  const handleTouchMove = (e: React.TouchEvent<HTMLDivElement>) => {
    const touch = e.touches[0]
    if (!touch || !touchStartX.current) return

    touchEndX.current = touch.clientX
    const distance = Math.abs(touchEndX.current - touchStartX.current)

    if (!hasMoved.current && distance > 10) {
      hasMoved.current = true
      setIsSwiping(true)
    }
  }

  const handleTouchEnd = () => {
    if (!touchStartX.current || !touchEndX.current) return

    const swipeDistance = touchEndX.current - touchStartX.current

    if (hasMoved.current && Math.abs(swipeDistance) >= MIN_SWIPE_DISTANCE) {
      if (swipeDistance > 0 && canScrollLeft) {
        handleScroll('left')
      } else if (swipeDistance < 0 && canScrollRight) {
        handleScroll('right')
      }
    }

    // Reset states
    touchStartX.current = null
    touchEndX.current = null
    hasMoved.current = false
    setIsSwiping(false)
  }

  const visibleChips = betChips.slice(startIndex, startIndex + VISIBLE_BUTTONS)
  const canScrollLeft = startIndex > 0
  const canScrollRight = startIndex + VISIBLE_BUTTONS < betChips.length

  return (
    <div className={`bet-amount_top ${disabled ? 'disabled' : ''}`}>
      <button
        className="btn btn--clear"
        disabled={disabled}
        onClick={() => {
          handleButtonClick(minAmount, 'set')
        }}
      >
        <span>{t('Min')}</span>
      </button>

      <div className="bet-amount-scroll-container">
        <button
          className="btn btn--scroll"
          disabled={disabled || !canScrollLeft}
          onClick={() => {
            handleScroll('left')
          }}
        >
          <AngleLeftIcon />
        </button>

        <div
          className={`bet-amount-buttons ${isSwiping ? 'swiping' : ''}`}
          onTouchEnd={handleTouchEnd}
          onTouchMove={handleTouchMove}
          onTouchStart={handleTouchStart}
        >
          {visibleChips.map((chip, index) => (
            <button
              className="btn btn--amount"
              key={startIndex + index}
              disabled={disabled}
              onClick={() => {
                handleButtonClick(chip.value, chip.type)
              }}
            >
              <span>
                {chip.type === 'increment' ? '+' : ''}
                {chip.label}
              </span>
            </button>
          ))}
        </div>

        <button
          className="btn btn--scroll"
          disabled={disabled || !canScrollRight}
          onClick={() => {
            handleScroll('right')
          }}
        >
          <AngleRightIcon />
        </button>
      </div>

      <button className="btn btn--double" disabled={disabled} onClick={handleDouble}>
        <span>{t('x2')}</span>
      </button>
    </div>
  )
}
